/**
 * Client-side permission helpers (ADR-0030).
 *
 * Both hooks read from the already-cached `identity.me` response — no extra
 * network round-trip. The server enforces the same grants via `permissionGate`;
 * these hooks are UX softening only (hide buttons, gate pages).
 *
 * `hasPermission` from `@flint/api-contract` is the single source of `*`
 * wildcard semantics — never inline the logic here.
 *
 * @see packages/api-contract/src/permissions.ts — catalog + hasPermission
 * @see apps/api/src/trpc.ts — permissionGate (server enforcement)
 */
import { useQuery } from "@tanstack/react-query";
import { hasPermission } from "@flint/api-contract";
import type { Permission } from "@flint/api-contract";
import { meQueryOptions } from "./session.ts";

/** Returns the current member's granted permission strings, or `[]` when
 *  unauthenticated / no company. Stable reference between renders when the
 *  `me` cache is unchanged. */
export function usePermissions(): readonly string[] {
  const { data } = useQuery(meQueryOptions);
  return data?.company?.permissions ?? [];
}

/** Returns `true` if the signed-in member holds `permission` (or `"*"`).
 *  Delegates to `hasPermission` so wildcard + exact match behave identically
 *  to the server gate. */
export function usePermission(permission: Permission): boolean {
  const permissions = usePermissions();
  return hasPermission(permissions, permission);
}

/**
 * Returns `true` when the signed-in member is the company owner (legacy
 * `Founder` role). Used to hide founder-scope UI affordances — e.g. the
 * Edit button on AI-authored reports. Enforcement is always server-side
 * (mutations still 403 for non-founders); this hook is display-only.
 */
export function useIsOwner(): boolean {
  const { data } = useQuery(meQueryOptions);
  return data?.company?.role === "Founder";
}
